import { Box, Text } from '@chakra-ui/react';
import SectionWrapper from '../../pass-templates-new/SectionWrapper';
import type { PassAttributes } from '../../../../store/types/pass';
import { getTimezoneAbbr } from '../utils/passHelpers';

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatTimestamp = (
  dateStr: string | null | undefined,
  fallback = 'n/a'
): string => {
  if (!dateStr) return fallback;
  const timestamp = new Date(dateStr);
  const day = timestamp.getDate();
  const month = timestamp.toLocaleString('en-GB', { month: 'long' });
  const year = String(timestamp.getFullYear()).slice(-2);
  const hours = String(timestamp.getHours()).padStart(2, '0');
  const minutes = String(timestamp.getMinutes()).padStart(2, '0');
  const tzAbbr = getTimezoneAbbr(LOCAL_TZ, timestamp);
  return `${day} ${month} ${year}, ${hours}.${minutes} ${tzAbbr}`;
};

interface ActivityItemProps {
  label: string;
  value?: string | null;
  fallback?: string;
  subtitle?: string | null;
}

const ActivityItem = ({
  label,
  value,
  fallback = 'n/a',
  subtitle,
}: ActivityItemProps) => (
  <Box>
    <Text textStyle="bodyFamilyRegular" fontSize="16px" color="black">
      {label}
    </Text>
    <Text
      textStyle="bodyFamilyRegular"
      fontSize="16px"
      color="#989898"
      mt="4px"
    >
      {value || fallback}
    </Text>
    {subtitle && (
      <Text textStyle="bodyFamilyRegular" fontSize="16px" color="#989898">
        {subtitle}
      </Text>
    )}
  </Box>
);

interface ActivitySectionProps {
  attributes?: PassAttributes;
}

const ActivitySection = ({ attributes }: ActivitySectionProps) => {
  const isExpired = attributes?.expiresAt
    ? new Date(attributes.expiresAt) < new Date()
    : false;

  return (
    <SectionWrapper title="Activity" openOnLoad id="activity-section">
      <Box display="flex" flexDirection="column" gap={6}>
        <ActivityItem
          label="Issue date"
          value={formatTimestamp(attributes?.createdAt)}
        />
        <ActivityItem
          label="Added to wallet date"
          value={formatTimestamp(attributes?.addedAt, 'Not yet added')}
        />
        <ActivityItem
          label="Last scanned"
          value={formatTimestamp(attributes?.lastScannedAt, 'No scans yet')}
        />
        {attributes?.status === 'deactivated' && !isExpired && (
          <ActivityItem
            label="Deactivated date"
            value={formatTimestamp(attributes?.deactivatedAt)}
          />
        )}
        {isExpired && (
          <ActivityItem
            label="Expired date"
            value={formatTimestamp(attributes?.expiresAt)}
          />
        )}
        {attributes?.status === 'removed' && (
          <ActivityItem
            label="Removed date"
            value={formatTimestamp(attributes?.removedAt)}
          />
        )}
      </Box>
    </SectionWrapper>
  );
};

export default ActivitySection;
